/**
 * Turtle Shack drink & food effects — visual auras that follow the avatar for a few minutes.
 * Synced to other players by piggybacking `street_fx: {id, until}` on avatar_update.
 */

const FX_BY_ITEM = {
  pina_colada: { fx: 'tropical', mins: 3 },
  beach_beer: { fx: 'bubbles', mins: 3 },
  blue_apple_beer: { fx: 'apple_trail', mins: 3 },
  turtlelini: { fx: 'turtle_aura', mins: 3 },
  turtle_burger: { fx: 'yum', mins: 1.5 },
  boardwalk_fries: { fx: 'yum', mins: 1.5 },
  coconut_shrimp: { fx: 'yum', mins: 1.5 },
  key_lime_pie: { fx: 'yum', mins: 1.5 },
  // Downtown Strip shops
  pizza_slice: { fx: 'yum', mins: 2 },
  pizza_supreme: { fx: 'yum', mins: 2 },
  garlic_knots: { fx: 'yum', mins: 1.5 },
  soda_pop: { fx: 'bubbles', mins: 2 },
  boba_classic: { fx: 'boba', mins: 3 },
  boba_taro: { fx: 'boba', mins: 3 },
  boba_matcha: { fx: 'boba', mins: 3 },
  ink_flame: { fx: 'ink_flame', mins: 10 },
  ink_halo: { fx: 'ink_halo', mins: 10 },
  ink_wings: { fx: 'ink_wings', mins: 10 },
};

export const fxForItem = (id) => FX_BY_ITEM[id] || null;

// beach beer gives a slightly wobbly walk
export const fxWobble = (fxId, ph) => (fxId === 'bubbles' ? Math.sin(ph * 0.11) * 2.2 : 0);

// deterministic pseudo-random
const pr = (n) => {
  const s = Math.sin(n * 127.1) * 43758.5453;
  return s - Math.floor(s);
};

export function drawDrinkEffect(ctx, fxId, x, y, ph) {
  if (!fxId) return;
  const t = ph;
  ctx.save();
  if (fxId === 'tropical') {
    // lei around the neck
    ctx.fillStyle = '#f472b6';
    for (let i = 0; i < 6; i++) {
      const a = Math.PI * (0.15 + (i / 5) * 0.7);
      const lx = x + Math.cos(a) * 12;
      const ly = y - 46 + Math.sin(a) * 6;
      ctx.fillStyle = i % 2 ? '#f472b6' : '#fde047';
      ctx.beginPath(); ctx.arc(lx, ly, 2.4, 0, Math.PI * 2); ctx.fill();
    }
    // floating hibiscus petals
    for (let i = 0; i < 5; i++) {
      const a = t * 0.02 + (i * Math.PI * 2) / 5;
      const px2 = x + Math.cos(a) * (20 + pr(i) * 8);
      const py2 = y - 34 + Math.sin(a * 1.3) * 16 - Math.sin(t * 0.03 + i) * 4;
      ctx.globalAlpha = 0.85;
      ctx.fillStyle = ['#fb7185', '#fbbf24', '#f472b6', '#fda4af', '#fde047'][i];
      ctx.beginPath();
      ctx.ellipse(px2, py2, 3.2, 1.8, a, 0, Math.PI * 2);
      ctx.fill();
    }
    ctx.globalAlpha = 1;
  } else if (fxId === 'bubbles') {
    for (let i = 0; i < 7; i++) {
      const cycle = ((t * (0.6 + pr(i) * 0.5) + i * 17) % 52);
      const bx = x + (pr(i * 3) - 0.5) * 34 + Math.sin(t * 0.05 + i) * 3;
      const by = y - 4 - cycle;
      const rr = 1.5 + pr(i * 7) * 2.2;
      ctx.globalAlpha = Math.max(0, 0.75 - cycle / 60);
      ctx.strokeStyle = '#fbbf24';
      ctx.lineWidth = 1;
      ctx.beginPath(); ctx.arc(bx, by, rr, 0, Math.PI * 2); ctx.stroke();
      ctx.fillStyle = 'rgba(253,224,71,0.35)';
      ctx.beginPath(); ctx.arc(bx, by, rr * 0.55, 0, Math.PI * 2); ctx.fill();
    }
    ctx.globalAlpha = 1;
  } else if (fxId === 'apple_trail') {
    for (let i = 0; i < 8; i++) {
      const cyc = ((t * 0.9 + i * 13) % 40);
      const sx = x + (pr(i * 5) - 0.5) * 26 - cyc * 0.3;
      const sy = y - 2 + (pr(i * 9) - 0.5) * 8 + cyc * 0.2;
      const sz = Math.max(0.5, 2.6 - cyc / 16);
      ctx.globalAlpha = Math.max(0, 0.9 - cyc / 40);
      ctx.fillStyle = i % 3 === 0 ? '#a3e635' : '#4ade80';
      ctx.save();
      ctx.translate(sx, sy);
      ctx.rotate(Math.PI / 4);
      ctx.fillRect(-sz, -sz, sz * 2, sz * 2);
      ctx.restore();
    }
    ctx.globalAlpha = 1;
  } else if (fxId === 'turtle_aura') {
    // pulsing green aura
    const pulse = 0.5 + Math.abs(Math.sin(t * 0.04)) * 0.5;
    ctx.globalAlpha = 0.16 * pulse;
    ctx.fillStyle = '#34d399';
    ctx.beginPath(); ctx.ellipse(x, y - 28, 26, 36, 0, 0, Math.PI * 2); ctx.fill();
    ctx.globalAlpha = 0.5;
    ctx.strokeStyle = '#34d399';
    ctx.lineWidth = 1.5;
    ctx.beginPath(); ctx.ellipse(x, y - 28, 26 + pulse * 4, 36 + pulse * 4, 0, 0, Math.PI * 2); ctx.stroke();
    ctx.globalAlpha = 1;
    // tiny shell backpack
    ctx.fillStyle = '#166534';
    ctx.beginPath(); ctx.ellipse(x - 13, y - 34, 6.5, 8, -0.25, 0, Math.PI * 2); ctx.fill();
    ctx.strokeStyle = '#22c55e';
    ctx.lineWidth = 1;
    ctx.beginPath(); ctx.ellipse(x - 13, y - 34, 4, 5.2, -0.25, 0, Math.PI * 2); ctx.stroke();
    ctx.beginPath();
    ctx.moveTo(x - 17, y - 37); ctx.lineTo(x - 9, y - 31);
    ctx.moveTo(x - 17, y - 31); ctx.lineTo(x - 9, y - 37);
    ctx.stroke();
  } else if (fxId === 'boba') {
    // rising tapioca pearls + pastel fizz
    for (let i = 0; i < 6; i++) {
      const cyc = ((t * (0.5 + pr(i) * 0.4) + i * 15) % 48);
      const bx = x + (pr(i * 4) - 0.5) * 30 + Math.sin(t * 0.05 + i) * 3;
      const by = y - 6 - cyc;
      ctx.globalAlpha = Math.max(0, 0.8 - cyc / 52);
      ctx.fillStyle = i % 2 ? '#78350f' : '#a78bfa';
      ctx.beginPath(); ctx.arc(bx, by, 2 + pr(i * 6) * 1.6, 0, Math.PI * 2); ctx.fill();
    }
    ctx.globalAlpha = 1;
  } else if (fxId === 'ink_flame') {
    // flickering flame aura at the feet
    for (let i = 0; i < 6; i++) {
      const fh = 8 + pr(i * 3) * 10 + Math.abs(Math.sin(t * 0.13 + i * 2)) * 8;
      const fx2 = x - 15 + i * 6;
      ctx.globalAlpha = 0.55;
      ctx.fillStyle = i % 2 ? '#f97316' : '#ef4444';
      ctx.beginPath();
      ctx.moveTo(fx2 - 3, y + 2); ctx.quadraticCurveTo(fx2, y - fh, fx2 + 3, y + 2);
      ctx.closePath(); ctx.fill();
      ctx.globalAlpha = 0.7;
      ctx.fillStyle = '#fde047';
      ctx.beginPath();
      ctx.moveTo(fx2 - 1.4, y + 2); ctx.quadraticCurveTo(fx2, y - fh * 0.55, fx2 + 1.4, y + 2);
      ctx.closePath(); ctx.fill();
    }
    ctx.globalAlpha = 1;
  } else if (fxId === 'ink_halo') {
    const bob2 = Math.sin(t * 0.06) * 2;
    ctx.globalAlpha = 0.9;
    ctx.strokeStyle = '#fbbf24';
    ctx.lineWidth = 3;
    ctx.shadowColor = '#fbbf24';
    ctx.shadowBlur = 8 + Math.abs(Math.sin(t * 0.08)) * 6;
    ctx.beginPath(); ctx.ellipse(x, y - 74 + bob2, 12, 4, 0, 0, Math.PI * 2); ctx.stroke();
    ctx.shadowBlur = 0;
    ctx.globalAlpha = 1;
  } else if (fxId === 'ink_wings') {
    const flap = Math.sin(t * 0.09) * 6;
    ctx.globalAlpha = 0.8;
    ctx.fillStyle = 'rgba(255,255,255,0.85)';
    [[-1, 0], [1, 0]].forEach(([sgn]) => {
      ctx.beginPath();
      ctx.moveTo(x + sgn * 8, y - 44);
      ctx.quadraticCurveTo(x + sgn * (26 + flap), y - 62 - flap, x + sgn * (30 + flap), y - 40);
      ctx.quadraticCurveTo(x + sgn * 20, y - 34, x + sgn * 8, y - 38);
      ctx.closePath(); ctx.fill();
    });
    ctx.strokeStyle = 'rgba(148,163,184,0.6)';
    ctx.lineWidth = 1;
    [[-1], [1]].forEach(([sgn]) => {
      ctx.beginPath();
      ctx.moveTo(x + sgn * 10, y - 42);
      ctx.quadraticCurveTo(x + sgn * (22 + flap), y - 54 - flap, x + sgn * (28 + flap), y - 41);
      ctx.stroke();
    });
    ctx.globalAlpha = 1;
  } else if (fxId === 'notes') {
    for (let i = 0; i < 4; i++) {
      const cyc = ((t * 0.6 + i * 18) % 52);
      const nx = x + (pr(i * 8) - 0.5) * 30 + Math.sin(t * 0.05 + i * 2) * 5;
      const ny = y - 48 - cyc * 0.8;
      ctx.globalAlpha = Math.max(0, 0.85 - cyc / 52);
      ctx.font = `${10 + pr(i) * 5}px sans-serif`;
      ctx.textAlign = 'center';
      ctx.fillStyle = i % 2 ? '#38bdf8' : '#a78bfa';
      ctx.fillText(i % 2 ? '♪' : '♫', nx, ny);
    }
    ctx.globalAlpha = 1;
  } else if (fxId === 'yum') {
    for (let i = 0; i < 3; i++) {
      const cyc = ((t * 0.7 + i * 21) % 46);
      const hx = x + (pr(i * 11) - 0.5) * 22 + Math.sin(t * 0.04 + i * 2) * 3;
      const hy = y - 52 - cyc * 0.7;
      ctx.globalAlpha = Math.max(0, 0.85 - cyc / 46);
      ctx.font = `${9 + pr(i) * 4}px sans-serif`;
      ctx.textAlign = 'center';
      ctx.fillText(i % 2 ? '💚' : '😋', hx, hy);
    }
    ctx.globalAlpha = 1;
  }
  ctx.restore();
}
